import React, { useEffect } from "react";
import styled, { css } from "styled-components";
import { motion } from "framer-motion";

// Метатеги
const MetaTags = () => (
  <>
    <meta name="viewport" content="width=device-width, initial-scale=1.0" />
    <meta name="description" content="Тарифы на услуги Сонет-Дом. Абонентская плата за обслуживание домофона (аудио и видео), перезаключение договора." />
    <meta name="keywords" content="Сонет-Дом, тарифы, абонентская плата, домофон, аудио, видео, перезаключение договора" />
  </>
);

// Глобальные стили для предотвращения горизонтального скроллинга
const GlobalStyles = styled.div`
  width: 100%;
  overflow-x: hidden;
`;

// Стиль для секции
const Section = styled(motion.section)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  background: #f4f4f4;
  color: #333;
  transition: background 0.5s ease;
  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

// Заголовок
const Title = styled.h1`
  font-size: 3rem;
  margin-bottom: 2rem;
  color: #007aff;
  animation: fadeIn 1s ease-in-out;
  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(-20px); }
    to { opacity: 1; transform: translateY(0); }
  }
  @media (max-width: 768px) {
    font-size: 2.5rem;
    margin-bottom: 1.5rem;
  }
`;

// Контейнер для тарифов
const TariffContainer = styled(motion.div)`
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  width: 90%;
  max-width: 600px;
  margin: 1rem;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: space-between;
  padding: 2rem;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 16px rgba(0, 0, 0, 0.15);
  }
  @media (max-width: 768px) {
    width: 100%;
    padding: 1.5rem;
  }
`;

// Подзаголовок
const Subtitle = styled.h2`
  font-size: 2rem;
  margin-bottom: 0.5rem;
  color: #333;
  @media (max-width: 768px) {
    font-size: 1.75rem;
    margin-bottom: 0.5rem;
  }
`;

// Описание тарифа
const TariffDescription = styled.p`
  font-size: 1rem;
  color: #555;
  margin-bottom: 1rem;
  line-height: 1.5;
  @media (max-width: 768px) {
    font-size: 0.9rem;
    margin-bottom: 1rem;
  }
`;

// Цены
const Price = styled.p`
  font-size: 1.5rem;
  color: #007aff;
  margin-bottom: 1rem;
  @media (max-width: 768px) {
    font-size: 1.25rem;
    margin-bottom: 1rem;
  }
`;

// Дополнительная информация
const AdditionalInfo = styled.p`
  font-size: 1rem;
  color: #333;
  margin-bottom: 0.5rem;
  line-height: 1.5;
  @media (max-width: 768px) {
    font-size: 0.9rem;
    margin-bottom: 0.5rem;
  }
`;

const Tariffs = () => {
  const sectionVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: "easeOut" } },
  };

  const tariffVariants = {
    hidden: { opacity: 0, y: 50, scale: 1 },
    visible: { opacity: 1, y: 0, scale: 1, transition: { duration: 0.6, ease: "easeOut" } },
    hover: { scale: 1.02, transition: { duration: 0.3, ease: "easeInOut" } },
  };

  // Эффект для прокрутки страницы вверх при загрузке
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <MetaTags />
      <GlobalStyles>
        <Section
          variants={sectionVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          bgColor="#f4f4f4"
          textColor="#333"
          style={{ marginTop: '60px' }} // Добавляем отступ сверху
        >
          <Title>Тарифы</Title>

          <TariffContainer
            variants={tariffVariants}
            initial="hidden"
            whileInView="visible"
            whileHover="hover"
            viewport={{ once: true }}
          >
            <Subtitle>Абонентская плата за обслуживание домофона (аудио)</Subtitle>
            <Price>90 руб. в месяц с 01.01.2026 г.</Price>
            <TariffDescription>Стоимость обслуживания аудио домофона, для клиентов заключивших договор с нашей компанией.</TariffDescription>
          </TariffContainer>

          <TariffContainer
            variants={tariffVariants}
            initial="hidden"
            whileInView="visible"
            whileHover="hover"
            viewport={{ once: true }}
          >
            <Subtitle>Абонентская плата за обслуживание домофона (видео)</Subtitle>
            <Price>180 руб. в месяц с 01.01.2026 г.</Price>
            <TariffDescription>Стоимость обслуживания видео домофона, для клиентов заключивших договор с нашей компанией.</TariffDescription>
          </TariffContainer>

          <TariffContainer
            variants={tariffVariants}
            initial="hidden"
            whileInView="visible"
            whileHover="hover"
            viewport={{ once: true }}
          >
            <Subtitle>Перезаключение договора</Subtitle>
            <AdditionalInfo>При смене собственника повторное подключение к услугам нашего сервиса (Перезаключение договора) составляет от 2500 руб.</AdditionalInfo>
            <AdditionalInfo>Повторное подключение к услугам сервиса (Перезаключение договора) для юридических лиц составляет от 5000 руб.</AdditionalInfo>
            <AdditionalInfo>Если договор заключается с 1 по 15 число месяца, абонентская плата начисляется с текущего месяца. Если заключение договора происходит с 16 числа и далее, плата начисляется с последующего месяца.</AdditionalInfo>
          </TariffContainer>
        </Section>
      </GlobalStyles>
    </>
  );
};

export default Tariffs;